import {
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@parrot-software/design-system';
import { CircleHelp } from '@parrot-software/design-system/icons';

type WizardFormFieldProps = {
  error?: string;
  id: string;
  label: string;
  onBlur?: () => void;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  tooltip?: string;
  value: string;
};

/**
 * A form field with label, input, and optional tooltip icon.
 * Used for text-based configuration in the wizard (e.g., receipt header).
 */
export const WizardFormField = ({
  error,
  id,
  label,
  onBlur,
  onChange,
  placeholder,
  required,
  tooltip,
  value,
}: WizardFormFieldProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <Label className={cn('text-sm font-semibold text-foreground', { 'whitespace-nowrap': required })} htmlFor={id}>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
        {tooltip && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="inline-flex cursor-help" type="button">
                  <CircleHelp className="h-4 w-4 text-info" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[256px] border bg-background text-foreground shadow-md">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <Input
        className={cn({ 'border-destructive': error })}
        id={id}
        onBlur={onBlur}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        value={value}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
